import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { isPaidPlan } from 'enums/user-plan';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Navbar, NavMenu, NavMenuLink, NavMenuButton } from 'platfi-ui-components';

import { UserType } from 'enums/user-type';
import { UserRole } from 'enums/user-role';
import { UserFeatureFlags, UserMembershipFeatures } from 'enums/user-membership-features';
import { UserPermissions } from 'enums/user-permissions';
import { PartnerType, toLabel, isLegacyPartner } from 'enums/partner-type';
import { TeamWizzardPaths } from 'enums/team-wizzard-paths';

import { makeSelectFeatureFlagActive } from 'reducers/featureFlags/selectors';
import { makeSelectMasquerade, makeSelectUserRoles } from 'reducers/auth/selectors';

import {
  makeSelectMarketplaceApiKey,
  makeSelectMarketplaceModalOpen,
  makeSelectMarketplaceModalOptions,
  makeSelectHasUser,
  makeSelectUserCompanyName,
  makeSelectUserConsultant,
  makeSelectUserFeature,
  makeSelectUserFundingSearchCount,
  makeSelectUserLevel,
  makeSelectUserName,
  makeSelectUserNetworkName,
  makeSelectUserPlan,
  makeSelectUserTeamIsDefaultTeam,
  makeSelectUserTeamName,
  makeSelectUserType,
  makeSelectUserCompanyId,
  makeSelectUserIntroducerToken,
  makeSelectUserEmail,
} from 'reducers/user/selectors';

import * as authActions from 'reducers/auth/reducer';
import * as teamsWizzardActions from 'reducers/teamsWizzard/actions';
import * as userActions from 'reducers/user/actions';
import FeatureFlag from 'modules/FeatureFlag';
import CanRender from 'modules/Permission/CanRender';
import * as monitorActions from 'reducers/monitor/connect-wizzard/actions';
import * as monitorClientsActions from 'reducers/monitor/reducer';

import withModalActions from 'HOC/withModalActions';

import IntroducerNavbar from './components/IntroducerNavbar/IntroducerNavbar';
import BusinessNavbar from './components/BusinessNavbar/BusinessNavbar';
import LenderNavbar from './components/LenderNavbar/LenderNavbar';

class LoggedNavbar extends Component {
  static propTypes = {
    apiKey: PropTypes.string,
    closeQuestionnaire: PropTypes.func.isRequired,
    demasquerade: PropTypes.func.isRequired,
    fundingSearchCount: PropTypes.number,
    hasDashboardAccess: PropTypes.bool,
    hasFundingAccess: PropTypes.bool,
    hasFundingReportAccess: PropTypes.bool,
    hasIntegrationAccess: PropTypes.bool,
    hasInviteAccess: PropTypes.bool,
    hasMeganavAccess: PropTypes.bool,
    hasMonitorAccess: PropTypes.bool,
    hasMonitoringAccess: PropTypes.bool,
    hasNetworkManagerRole: PropTypes.bool,
    hasPlaybookAccess: PropTypes.bool,
    hasWorkingCapitalAccess: PropTypes.bool,
    hasUser: PropTypes.bool,
    isLearningEnabled: PropTypes.bool,
    isQuestionnaireOpen: PropTypes.bool,
    logout: PropTypes.func.isRequired,
    masquerade: PropTypes.bool,
    openModal: PropTypes.func.isRequired,
    openMonitorWizzard: PropTypes.func.isRequired,
    openQuestionnaire: PropTypes.func.isRequired,
    openTeamsWizzard: PropTypes.func.isRequired,
    planName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    questionnaireOptions: PropTypes.object,
    requestApiKey: PropTypes.func.isRequired,
    userCompanyName: PropTypes.string,
    userConsultant: PropTypes.object,
    userLevel: PropTypes.instanceOf(Immutable.Map),
    userName: PropTypes.string,
    userNetworkName: PropTypes.string,
    userPlan: PropTypes.string,
    userRoles: PropTypes.oneOfType([PropTypes.instanceOf(Immutable.List), PropTypes.array]),
    userTeamIsDefaultTeam: PropTypes.bool,
    userTeamName: PropTypes.string,
    userType: PropTypes.string,
    entityId: PropTypes.number,
    introducerToken: PropTypes.string,
    userEmail: PropTypes.string,
    requestClients: PropTypes.func,
  };

  static getDerivedStateFromProps({ userRoles }) {
    return {
      isCompanyManager: userRoles.includes(UserRole.companyManager),
      isNetworkManager: userRoles.includes(UserRole.networkManager),
    };
  }

  state = {
    isCompanyManager: false,
    isNetworkManager: false,
  };

  renderNavMenuLinks = () => {
    const { isCompanyManager, isNetworkManager } = this.state;
    const {
      demasquerade,
      hasIntegrationAccess,
      hasInviteAccess,
      logout,
      masquerade,
      openTeamsWizzard,
      userPlan,
      userType,
    } = this.props;

    const isIntroducer = userType === UserType.introducer;

    return (
      <Fragment>
        {isIntroducer && (
          <FeatureFlag flag="user_profile">
            <NavMenuLink icon="user_placeholder" label="Edit Profile" to="/settings/edit-profile" />
          </FeatureFlag>
        )}

        {isIntroducer && isPaidPlan(userPlan) && (
          <FeatureFlag flag="teams">
            <CanRender permission={UserPermissions.canViewMyTeam} options={{ skipHandlers: true }}>
              <NavMenuLink icon="group_users" label="My Team" to="/settings/my-team" />
            </CanRender>
          </FeatureFlag>
        )}

        {isIntroducer && isPaidPlan(userPlan) && isNetworkManager && (
          <FeatureFlag flag="teams">
            <CanRender
              permission={UserPermissions.canViewAllCompanies}
              options={{ skipHandlers: true }}
            >
              <NavMenuLink icon="network" label="My Network" to="/settings/my-network-teams" />
            </CanRender>
          </FeatureFlag>
        )}

        {isIntroducer && isCompanyManager && !isNetworkManager && (
          <FeatureFlag flag="teams">
            <CanRender
              permission={UserPermissions.canViewAllTeams}
              options={{ skipHandlers: true }}
            >
              <NavMenuLink icon="network" label="All Teams" to="/settings/my-company-teams" />
            </CanRender>
          </FeatureFlag>
        )}

        {isIntroducer && hasIntegrationAccess && (
          <NavMenuLink icon="settings" label="Website Integration" to="/settings/widget" />
        )}

        {userType === UserType.lender && (
          <NavMenuLink
            medium
            fullWidth
            icon="plus_alt"
            label="Invite team members"
            to="/dashboard/invite"
            onClick={() => openTeamsWizzard(TeamWizzardPaths.addNewUser)}
          />
        )}

        {isIntroducer && hasInviteAccess && (
          <>
            <FeatureFlag flag="teams" not>
              <NavMenuButton
                onClick={() => openTeamsWizzard(TeamWizzardPaths.addTeamMember)}
                label="Invite team members"
                icon="plus_alt"
                medium
                outlined
                primary
                fullWidth
              />
            </FeatureFlag>

            <FeatureFlag flag="teams">
              <CanRender
                permission={UserPermissions.canInviteMembersToMyTeam}
                options={{ skipHandlers: true }}
              >
                <NavMenuButton
                  onClick={() => openTeamsWizzard(TeamWizzardPaths.addTeamMember)}
                  label="Invite team members"
                  icon="plus_alt"
                  medium
                  outlined
                  primary
                  fullWidth
                />
              </CanRender>
            </FeatureFlag>
          </>
        )}

        {masquerade && (
          <NavMenuLink icon="round_user" label="Demasquerade" onClick={demasquerade} />
        )}

        <NavMenuLink icon="logout" label="Logout" onClick={logout} />
      </Fragment>
    );
  };

  renderNavMenu = () => {
    const {
      userName,
      userLevel,
      userTeamName,
      userCompanyName,
      userNetworkName,
      userTeamIsDefaultTeam,
    } = this.props;

    const userInfo = {
      userName,
      userTeamName,
      userCompanyName,
      userNetworkName,
      userTeamIsDefaultTeam,
    };

    const userLevelKey = userLevel.get('key');
    const isUserLegacyPartner = isLegacyPartner(userLevelKey);

    return (
      <NavMenu
        links={this.renderNavMenuLinks()}
        partnerType={
          isUserLegacyPartner && userLevelKey !== PartnerType.light && toLabel(userLevelKey)
        }
        new={isUserLegacyPartner && userLevelKey === PartnerType.new}
        gold={isUserLegacyPartner && userLevelKey === PartnerType.gold}
        silver={isUserLegacyPartner && userLevelKey === PartnerType.silver}
        {...userInfo}
      />
    );
  };

  componentDidUpdate(prevProps) {
    const { apiKey, requestApiKey, userType } = this.props;

    if (
      !apiKey &&
      !prevProps.userType &&
      userType &&
      [UserType.introducer, UserType.business].includes(userType)
    ) {
      requestApiKey();
    }
  }

  render() {
    const {
      apiKey,
      closeQuestionnaire,
      fundingSearchCount,
      hasDashboardAccess,
      hasFundingAccess,
      hasFundingReportAccess,
      hasMonitorAccess,
      hasMonitoringAccess,
      hasMeganavAccess,
      hasPlaybookAccess,
      hasWorkingCapitalAccess,
      entityId,
      hasUser,
      isLearningEnabled,
      isQuestionnaireOpen,
      openModal,
      openMonitorWizzard,
      openQuestionnaire,
      planName,
      questionnaireOptions,
      userConsultant,
      userLevel,
      userType,
      introducerToken,
      userName,
      userEmail,
      requestClients,
    } = this.props;

    const commonProps = {
      dark: true,
      isLearningEnabled,
      logoUrl: '/dashboard',
      planName,
    };

    // If no user was returned yet we display an empty dashboard
    if (!hasUser) {
      return <Navbar {...commonProps} links={[]} />;
    }

    switch (userType) {
      case UserType.introducer:
        return (
          <IntroducerNavbar
            {...commonProps}
            apiKey={apiKey}
            closeQuestionnaire={closeQuestionnaire}
            consultant={userConsultant}
            fundingSearchCount={fundingSearchCount}
            hasDashboardAccess={hasDashboardAccess}
            hasFundingAccess={hasFundingAccess}
            hasFundingReportAccess={hasFundingReportAccess}
            hasMeganavAccess={hasMeganavAccess}
            hasMonitorAccess={hasMonitorAccess}
            hasMonitoringAccess={hasMonitoringAccess}
            hasPlaybookAccess={hasPlaybookAccess}
            hasWorkingCapitalAccess={hasWorkingCapitalAccess}
            isQuestionnaireOpen={isQuestionnaireOpen}
            navMenu={this.renderNavMenu()}
            openModal={openModal}
            openMonitorWizzard={openMonitorWizzard}
            openQuestionnaire={openQuestionnaire}
            partnerType={userLevel.get('key')}
            questionnaireOptions={questionnaireOptions}
            introducerToken={introducerToken}
            userName={userName}
            userEmail={userEmail}
            requestClients={requestClients}
            entityId={entityId}
          />
        );
      case UserType.business:
        return (
          <BusinessNavbar
            {...commonProps}
            apiKey={apiKey}
            navMenu={this.renderNavMenu()}
            closeQuestionnaire={closeQuestionnaire}
            isQuestionnaireOpen={isQuestionnaireOpen}
            questionnaireOptions={questionnaireOptions}
            entityId={entityId}
          />
        );
      case UserType.lender:
        return (
          <LenderNavbar
            {...commonProps}
            navMenu={this.renderNavMenu()}
            fundingSearchCount={fundingSearchCount}
          />
        );
      default:
        return <Navbar {...commonProps} />;
    }
  }
}

const mapStateToProps = createStructuredSelector({
  apiKey: makeSelectMarketplaceApiKey(),
  fundingSearchCount: makeSelectUserFundingSearchCount(),

  hasDashboardAccess: makeSelectUserFeature(UserMembershipFeatures.dealDashboard),
  hasFundingAccess: makeSelectUserFeature(UserMembershipFeatures.productFunding),
  hasFundingReportAccess: makeSelectUserFeature(UserMembershipFeatures.fundingReport),
  hasIntegrationAccess: makeSelectUserFeature(
    UserMembershipFeatures.integrationsWebsiteQuestionnaire,
  ),
  hasInviteAccess: makeSelectUserFeature(UserMembershipFeatures.featureUserInvitations),
  hasMonitorAccess: makeSelectUserFeature(UserMembershipFeatures.productMonitor),
  hasMonitoringAccess: makeSelectUserFeature(UserMembershipFeatures.monitoring),
  hasMeganavAccess: makeSelectUserFeature(UserMembershipFeatures.featureMeganav),
  hasPlaybookAccess: makeSelectUserFeature(UserMembershipFeatures.playbook),
  hasWorkingCapitalAccess: makeSelectUserFeature(UserMembershipFeatures.featureWorkingCapital),
  hasUser: makeSelectHasUser(),
  isLearningEnabled: makeSelectFeatureFlagActive(UserFeatureFlags.learning),
  isQuestionnaireOpen: makeSelectMarketplaceModalOpen(),
  questionnaireOptions: makeSelectMarketplaceModalOptions(),
  masquerade: makeSelectMasquerade(),
  planName: makeSelectUserFeature(UserMembershipFeatures.planFriendlyName),
  hasCapitalReportAccess: makeSelectFeatureFlagActive(UserFeatureFlags.capitalReport),
  userCompanyName: makeSelectUserCompanyName(),
  userConsultant: makeSelectUserConsultant(),
  userLevel: makeSelectUserLevel(),
  userName: makeSelectUserName(),
  userEmail: makeSelectUserEmail(),
  userNetworkName: makeSelectUserNetworkName(),
  userPlan: makeSelectUserPlan(),
  userRoles: makeSelectUserRoles(),
  userTeamIsDefaultTeam: makeSelectUserTeamIsDefaultTeam(),
  userTeamName: makeSelectUserTeamName(),
  userType: makeSelectUserType(),
  entityId: makeSelectUserCompanyId(),
  introducerToken: makeSelectUserIntroducerToken(),
});

export function mapDispatchToProps(dispatch) {
  return {
    closeQuestionnaire: () => dispatch(userActions.closeQuestionnaire()),
    logout: () => dispatch(authActions.logout()),
    demasquerade: () => dispatch(authActions.demasquerade()),
    openTeamsWizzard: (startPath) => dispatch(teamsWizzardActions.openTeamsWizzard(startPath)),
    openMonitorWizzard: (startPath) => dispatch(monitorActions.openMonitorWizzard(startPath)),
    openQuestionnaire: (options) => dispatch(userActions.openQuestionnaire(options)),
    requestApiKey: () => dispatch(userActions.requestApiKey()),
    requestClients: (entityId, archived) =>
      dispatch(monitorClientsActions.requestAllClients(entityId, archived)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withModalActions(LoggedNavbar));
